// Settings, Atlas Assistant (Console V5): a person's own rules, what they ran, their AI use
// today, and the owner's travel and spend policy. Served by src/aithora/v3/settings/assistant-rules.ts
// (the Aithora team only; everyone else gets a plain 403) and src/aithora/assistant-routes.ts
// (travel policy: the team reads it, the owner alone changes it). The role comes from the session
// on the server. Watches, tasks and revoke-all arrive with the V5 back end (branch backend-v5).
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';

const A = AITHORA_PREFIX;

export type AssistantRuleMode = 'allow' | 'ask' | 'block';

export interface AssistantRule {
  id: string;
  tool: string;
  mode: AssistantRuleMode;
  conditions: { to_domains?: string[]; attendee_domains?: string[]; max_minutes?: number };
  note: string | null;
  expiresAt: string | null;
  createdAt: string;
  /** Where it was made ('settings', 'text', 'assistant', ...). */
  createdFrom?: string;
  /** Until then an allow rule only logs what it would have done (the practice week). */
  practiceUntil?: string | null;
  /** Actions it ran on its own, and how many of those the person stopped with Undo. */
  runCount?: number;
  undoCount?: number;
  /** The rule in one sentence ("Always allow send emails (only to @aithoracare.com)"). */
  sentence: string;
}

export interface AssistantRulesResponse {
  rules: AssistantRule[];
  /** What a rule may cover, with the conditions each kind takes. */
  kinds: { tool: string; label: string; conditions: string[] }[];
}

export interface AssistantRuleRun {
  what: string;
  status: string;
  outcome: string | null;
  at: string;
  because: string | null;
}

export interface AssistantUsage {
  day: string;
  costUsd: number;
  calls: number;
  capUsd: number;
}

export interface AssistantTeamUsage {
  userId: string;
  name: string;
  email: string;
  costUsd: number;
  calls: number;
  capUsd: number;
  hitsCapAt: string | null;
  hitsCapLocal: string | null;
}

export type WatchLevel = 'more' | 'normal' | 'less' | 'off';

export interface AssistantWatch {
  id: string;
  kind: string;
  title: string;
  level: WatchLevel;
  lastCheckedAt: string | null;
  lastFiredAt: string | null;
  lastStatus: string | null;
}

export interface AssistantTask {
  id: string;
  title: string;
  /** The schedule in words ("Every weekday at 7:00 AM"). */
  when: string;
  timezone: string;
  deliver: string;
  nextRun: string | null;
  lastRun: string | null;
  lastStatus: string | null;
  runs: number;
}

export type TravelCabin = 'economy' | 'premium_economy' | 'business' | 'first';

export interface TravelPolicy {
  allowedCabins: TravelCabin[];
  maxFarePerPersonUsd: number;
  hotelMaxNightUsd: number | null;
  hotelMaxWeekUsd: number;
  travellerUserIds: string[];
  preferredAirlines: string[];
  ownerOkAboveUsd: number;
}

/** GET /assistant/rules: the caller's active rules. */
export const assistantRules = route<AssistantRulesResponse>()('GET', `${A}/assistant/rules`);
/** POST /assistant/rules {tool, mode, conditions?, hours?}: save one (the caller's own click). */
export const assistantRuleSave = route<{ rule: AssistantRule; text: string }, void, { tool: string; mode: AssistantRuleMode; conditions?: Record<string, unknown>; hours?: number }>()(
  'POST', `${A}/assistant/rules`);
/** DELETE /assistant/rules/:ruleId: revoke one of the caller's own. */
export const assistantRuleRevoke = route<{ revoked: true }>()('DELETE', `${A}/assistant/rules/:ruleId`);
/** POST /assistant/rules/revoke-all: the owner only, everyone's rules at once (no passkey). */
export const assistantRulesRevokeAll = route<{ real: true; revoked: number; stopped: number }>()('POST', `${A}/assistant/rules/revoke-all`, { honest: true });
/** GET /assistant/watches: the caller's watches, each with its own level. */
export const assistantWatches = route<{ real: true; level: WatchLevel; dailyCap: number; watches: AssistantWatch[] }>()('GET', `${A}/assistant/watches`, { honest: true });
/** PATCH /assistant/watches/:id {level}. */
export const assistantWatchLevel = route<{ real: true; watch: Pick<AssistantWatch, 'id' | 'kind' | 'title' | 'level'> }, void, { level: WatchLevel }>()('PATCH', `${A}/assistant/watches/:id`, { honest: true });
/** DELETE /assistant/watches/:id: stop watching. */
export const assistantWatchStop = route<{ real: true; removed: true }>()('DELETE', `${A}/assistant/watches/:id`, { honest: true });
/** GET /assistant/tasks: the caller's scheduled tasks. */
export const assistantTasks = route<{ real: true; tasks: AssistantTask[] }>()('GET', `${A}/assistant/tasks`, { honest: true });
/** POST /assistant/tasks/:id/cancel. */
export const assistantTaskCancel = route<{ real: true; cancelled: true }>()('POST', `${A}/assistant/tasks/:id/cancel`, { honest: true });
/** GET /assistant/rule-runs: what the caller's rules ran lately. */
export const assistantRuleRuns = route<{ runs: AssistantRuleRun[] }>()('GET', `${A}/assistant/rule-runs`);
/** GET /assistant/usage: the caller's AI use today. */
export const assistantUsage = route<AssistantUsage>()('GET', `${A}/assistant/usage`);
/** GET /assistant/usage/team: the owner only, everyone's use today with when each hits the cap. */
export const assistantTeamUsage = route<{ people: AssistantTeamUsage[] }>()('GET', `${A}/assistant/usage/team`);
/** GET /api/assistant/travel-policy: the team reads it; canEdit is the owner's alone. */
export const travelPolicy = route<{ real: true; canEdit?: boolean; cabins: TravelCabin[]; policy: TravelPolicy }>()('GET', '/api/assistant/travel-policy', { honest: true });
/** PUT /api/assistant/travel-policy: the owner only, with a reason and a fresh passkey; the server rejects everyone else. */
export const travelPolicySave = route<{ real: true; canEdit?: boolean; policy: TravelPolicy; ledgerEntryId?: string | null }, void, Partial<TravelPolicy> & { reason: string; stepUpToken?: string; webauthnAssertion?: unknown }>()(
  'PUT', '/api/assistant/travel-policy', { honest: true });
